import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MdArrowBack,
  MdOpenInNew,
  MdShare,
  MdContentCopy,
  MdExpandMore,
} from "react-icons/md";
import Chart from "react-apexcharts";
import Card from "components/card";
import InformeDocente from "./InformeDocente";
import {
  reskillingService,
  ReskillingInforme,
  Cluster,
  ClusterCompetencia,
} from "services/reskillingService";

const InformeDetalle = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [informe, setInforme] = useState<ReskillingInforme | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;

    let cancelado = false;

    reskillingService
      .obtenerInforme(sessionId)
      .then((data) => {
        if (!cancelado) setInforme(data);
      })
      .catch((err) => {
        if (!cancelado) {
          setError(
            err instanceof Error ? err.message : "No se pudo cargar el informe."
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [sessionId]);

  if (cargando) {
    return (
      <div className="mt-10 text-center text-gray-500">Cargando informe…</div>
    );
  }

  if (error || !informe) {
    return (
      <div className="mt-5">
        <Volver onClick={() => navigate(-1)} />
        <Card extra="p-6 mt-4">
          <p className="text-sm text-red-600">
            {error || "No se encontró el informe."}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Volver onClick={() => navigate(-1)} />
        <BarraCompartir
          sessionId={sessionId!}
          urlInicial={informe.url_publica ?? null}
        />
      </div>

      <CuerpoInforme informe={informe} />
    </div>
  );
};

/** Genera, copia y revoca el link público del informe. */
function BarraCompartir({
  sessionId,
  urlInicial,
}: {
  sessionId: string;
  urlInicial: string | null;
}) {
  const [url, setUrl] = useState<string | null>(urlInicial);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const avisar = (texto: string) => {
    setAviso(texto);
    setTimeout(() => setAviso(null), 3000);
  };

  const generar = async () => {
    setTrabajando(true);
    try {
      const nueva = await reskillingService.compartirInforme(sessionId);
      setUrl(nueva);
      if (nueva) {
        await navigator.clipboard.writeText(nueva).catch(() => {});
        avisar("Link copiado al portapapeles.");
      }
    } catch (err) {
      avisar(err instanceof Error ? err.message : "No se pudo generar el link.");
    } finally {
      setTrabajando(false);
    }
  };

  const revocar = async () => {
    setTrabajando(true);
    try {
      await reskillingService.compartirInforme(sessionId, true);
      setUrl(null);
      avisar("El link dejó de funcionar.");
    } catch {
      avisar("No se pudo revocar el link.");
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {aviso && <span className="text-green-600">{aviso}</span>}

      {url ? (
        <>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(url).catch(() => {});
              avisar("Link copiado.");
            }}
            className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50"
          >
            <MdContentCopy /> Copiar link
          </button>
          <button
            type="button"
            onClick={revocar}
            disabled={trabajando}
            className="text-xs text-gray-400 hover:text-red-500 disabled:opacity-50"
          >
            Revocar
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={generar}
          disabled={trabajando}
          className="flex items-center gap-1 rounded-md bg-brand-500 px-3 py-1.5 text-white hover:bg-brand-600 disabled:opacity-60"
        >
          <MdShare />
          {trabajando ? "Generando…" : "Compartir informe"}
        </button>
      )}
    </div>
  );
}

/**
 * Cuerpo del informe. Lo comparten la vista del panel y la pública, que solo
 * difieren en el encabezado y en si muestran datos de contacto.
 */
export function CuerpoInforme({ informe }: { informe: ReskillingInforme }) {
  const { persona, metrics, archetype, ai_potential_index: iaIndex } = informe;

  return (
    <div className="space-y-5">
      {/* Encabezado común a los dos tipos de informe */}
      {/* Encabezado */}
      <Card extra="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
              {persona.nombre || persona.email}
            </h1>
            <p className="text-sm text-gray-500">{persona.email}</p>
            {informe.completada_el && (
              <p className="mt-1 text-xs text-gray-400">
                Completada el{" "}
                {new Date(informe.completada_el).toLocaleDateString("es-AR")}
              </p>
            )}
          </div>

          {typeof metrics?.match_global === "number" && (
            <div className="text-center">
              <p className="text-4xl font-bold text-brand-500">
                {metrics.match_global}%
              </p>
              <p className="text-xs uppercase text-gray-500">Match global</p>
            </div>
          )}
        </div>

        {metrics?.perfil_predominante && (
          <p className="mt-4 text-base font-semibold text-navy-700 dark:text-white">
            {metrics.perfil_predominante}
          </p>
        )}
        {metrics?.sintesis_perfil && (
          <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {metrics.sintesis_perfil}
          </p>
        )}
      </Card>

      {/* La evaluación de docentes tiene otra estructura: tramos y niveles
          con autopercepción contra desempeño, en vez de competencias. */}
      {informe.tipo === "docente" ? (
        <InformeDocente informe={informe} />
      ) : (
        <CuerpoCompetencias
          informe={informe}
          metrics={metrics}
          archetype={archetype}
          iaIndex={iaIndex}
        />
      )}
    </div>
  );
}

/** Informe de competencias generales: radar, clusters, tipología e índice IA. */
function CuerpoCompetencias({
  informe,
  metrics,
  archetype,
  iaIndex,
}: {
  informe: ReskillingInforme;
  metrics: ReskillingInforme["metrics"];
  archetype: ReskillingInforme["archetype"];
  iaIndex: ReskillingInforme["ai_potential_index"];
}) {
  const clusters = informe.clusters ?? [];

  return (
    <div className="space-y-5">
      {/* Radar + promedios por dominio */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card extra="p-6 lg:col-span-2">
          <h2 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
            Perfil de competencias
          </h2>
          <RadarCompetencias clusters={clusters} />
        </Card>

        <Card extra="p-6">
          <h2 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
            Por dominio
          </h2>
          <div className="space-y-4">
            {clusters.map((c) => (
              <div key={c.clave}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {c.icon} {c.label}
                  </span>
                  <span
                    className="font-bold"
                    style={{ color: c.colores.main }}
                  >
                    {c.promedio}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${c.promedio}%`,
                      background: `linear-gradient(90deg, ${c.colores.gradFrom}, ${c.colores.gradTo})`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Detalle por cluster */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {clusters.map((c) => (
          <Card key={c.clave} extra="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3
                className="text-base font-bold"
                style={{ color: c.colores.deep }}
              >
                {c.icon} {c.label}
              </h3>
              <span
                className="rounded-full px-3 py-1 text-sm font-bold"
                style={{ backgroundColor: c.colores.soft, color: c.colores.deep }}
              >
                {c.promedio}
              </span>
            </div>

            <div className="space-y-3">
              {c.competencias.map((comp) => (
                <FilaCompetencia
                  key={comp.nombre}
                  competencia={comp}
                  color={c.colores.main}
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Tipología */}
      {archetype?.tipologia_primaria && (
        <Card extra="p-6">
          <h2 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
            Tipología
          </h2>
          <div className="flex flex-wrap gap-4">
            <Tipologia dato={archetype.tipologia_primaria} principal />
            {archetype.tipologia_secundaria && (
              <Tipologia dato={archetype.tipologia_secundaria} />
            )}
          </div>

          {archetype.descripcion_personalizada && (
            <p className="mt-4 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {archetype.descripcion_personalizada}
            </p>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Bloque
              titulo="Fortaleza distintiva"
              texto={archetype.fortaleza_distintiva}
            />
            <Bloque
              titulo="Riesgo principal"
              texto={archetype.riesgo_principal}
            />
            <Bloque
              titulo="Desarrollo sugerido"
              texto={archetype.desarrollo_sugerido}
            />
            {Array.isArray(archetype.roles_ideales) && (
              <Bloque
                titulo="Roles ideales"
                texto={archetype.roles_ideales.join(" · ")}
              />
            )}
          </div>
        </Card>
      )}

      {/* Índice de potencial con IA */}
      {iaIndex?.indice_potencial !== undefined && (
        <Card extra="p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Potencial con herramientas de IA
            </h2>
            <div className="text-right">
              <span className="text-3xl font-bold text-brand-500">
                {iaIndex.indice_potencial}
              </span>
              <p className="text-xs text-gray-500">
                {iaIndex.nivel} {iaIndex.rango && `(${iaIndex.rango})`}
              </p>
            </div>
          </div>

          {iaIndex.perfil_descripcion && (
            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {iaIndex.perfil_descripcion}
            </p>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {iaIndex.area_desarrollo_prioritaria && (
              <Bloque
                titulo={`Área prioritaria: ${iaIndex.area_desarrollo_prioritaria.competencia}`}
                texto={iaIndex.area_desarrollo_prioritaria.descripcion}
              />
            )}
            {iaIndex.tiempo_estimado_autonomia && (
              <Bloque
                titulo="Tiempo estimado a la autonomía"
                texto={iaIndex.tiempo_estimado_autonomia}
              />
            )}
          </div>

          {Array.isArray(iaIndex.recomendaciones) &&
            iaIndex.recomendaciones.length > 0 && (
              <Lista titulo="Recomendaciones" items={iaIndex.recomendaciones} />
            )}

          {Array.isArray(iaIndex.herramientas_sugeridas) &&
            iaIndex.herramientas_sugeridas.length > 0 && (
              <Lista
                titulo="Herramientas sugeridas"
                items={iaIndex.herramientas_sugeridas}
              />
            )}
        </Card>
      )}

      {informe.report_url && (
        <div className="pb-4 text-center">
          <a
            href={informe.report_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-brand-500"
          >
            Ver el informe original <MdOpenInNew />
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Radar con las competencias agrupadas por dominio: se recorren los clusters en
 * orden para que cada sector del polígono corresponda a un dominio.
 */
function RadarCompetencias({ clusters }: { clusters: Cluster[] }) {
  const competencias = clusters.flatMap((c) => c.competencias);

  if (competencias.length === 0) {
    return <p className="text-sm text-gray-500">Sin competencias para mostrar.</p>;
  }

  return (
    <Chart
      type="radar"
      height={460}
      series={[
        {
          name: "Puntaje",
          data: competencias.map((c) => c.puntaje),
        },
      ]}
      options={{
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        labels: competencias.map((c) => c.nombre),
        // Un color por competencia según su dominio, para que se lea a qué
        // grupo pertenece cada vértice.
        markers: {
          size: 4,
          colors: clusters.flatMap((c) =>
            c.competencias.map(() => c.colores.main)
          ),
          strokeColors: "#fff",
          strokeWidth: 2,
        },
        colors: ["#4318FF"],
        fill: { opacity: 0.25 },
        stroke: { width: 2 },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          labels: { formatter: (v: number) => `${v}` },
        },
        plotOptions: {
          radar: {
            polygons: {
              strokeColors: "#e2e8f0",
              connectorColors: "#e2e8f0",
            },
          },
        },
        dataLabels: {
          enabled: true,
          background: { enabled: true, borderRadius: 4 },
          style: { fontSize: "11px" },
        },
        tooltip: { y: { formatter: (v: number) => `${v} / 100` } },
      }}
    />
  );
}

/**
 * Una competencia con su puntaje y, si el proveedor la manda, su análisis
 * personalizado. La descripción va colapsada para que el informe se pueda
 * recorrer de un vistazo.
 */
function FilaCompetencia({
  competencia,
  color,
}: {
  competencia: ClusterCompetencia;
  color: string;
}) {
  const [abierta, setAbierta] = useState(false);
  const tieneDescripcion = Boolean(competencia.descripcion);

  return (
    <div>
      <button
        type="button"
        onClick={() => tieneDescripcion && setAbierta((v) => !v)}
        className={`w-full text-left ${tieneDescripcion ? "cursor-pointer" : "cursor-default"}`}
        aria-expanded={tieneDescripcion ? abierta : undefined}
      >
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
            {competencia.nombre}
            {tieneDescripcion && (
              <MdExpandMore
                className={`transition-transform ${abierta ? "rotate-180" : ""}`}
              />
            )}
          </span>
          <span className="font-semibold text-gray-600 dark:text-gray-300">
            {competencia.puntaje}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
          <div
            className="h-full rounded-full"
            style={{ width: `${competencia.puntaje}%`, backgroundColor: color }}
          />
        </div>
      </button>

      {abierta && competencia.descripcion && (
        <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {competencia.descripcion}
        </p>
      )}
    </div>
  );
}

function Tipologia({
  dato,
  principal = false,
}: {
  dato: { nombre?: string; icono?: string; match?: number; tagline?: string };
  principal?: boolean;
}) {
  return (
    <div
      className={`flex-1 rounded-lg border p-4 ${
        principal
          ? "border-brand-200 bg-brand-50/50"
          : "border-gray-200 bg-gray-50/50"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">{dato.icono}</span>
        <div>
          <p className="font-bold text-navy-700">{dato.nombre}</p>
          {typeof dato.match === "number" && (
            <p className="text-xs text-gray-500">{dato.match}% de afinidad</p>
          )}
        </div>
      </div>
      {dato.tagline && (
        <p className="mt-2 text-xs text-gray-600">{dato.tagline}</p>
      )}
    </div>
  );
}

function Bloque({ titulo, texto }: { titulo: string; texto?: string }) {
  if (!texto) return null;

  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase text-gray-500">
        {titulo}
      </p>
      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {texto}
      </p>
    </div>
  );
}

function Lista({ titulo, items }: { titulo: string; items: string[] }) {
  return (
    <div className="mt-4">
      <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
        {titulo}
      </p>
      <ul className="list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-gray-300">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function Volver({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-sm text-gray-500 hover:text-brand-500"
    >
      <MdArrowBack /> Volver
    </button>
  );
}

export default InformeDetalle;
